import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { projectsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  FileText,
  Download,
  Building2,
  Leaf,
  Award,
  CheckCircle,
  AlertCircle,
  TrendingDown,
  Star,
  Target,
  FileDown,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';

const Reports = () => {
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState('none');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const COLORS = ['#0F2818', '#800020', '#10B981', '#3B82F6', '#8B5CF6'];

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await projectsAPI.getAll();
      const projectsWithOptions = response.data.filter(
        (p) => p.design_options && p.design_options.length > 0
      );
      setProjects(projectsWithOptions);
    } catch (error) {
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateReport = async () => {
    if (!selectedProjectId || selectedProjectId === 'none') {
      toast.error('Please select a project');
      return;
    }

    setGenerating(true);
    try {
      const response = await projectsAPI.getReport(selectedProjectId);
      setReport(response.data);
      toast.success('Report generated successfully');
    } catch (error) {
      toast.error('Failed to generate report');
    } finally {
      setGenerating(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!selectedProjectId || selectedProjectId === 'none') {
      toast.error('Please select a project');
      return;
    }

    setDownloadingPdf(true);
    try {
      const response = await projectsAPI.downloadPdfReport(selectedProjectId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${report?.project?.name?.replace(/\s+/g, '_') || 'Sustainability'}_Report.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded successfully');
    } catch (error) {
      toast.error('Failed to download PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="w-8 h-8 border-4 border-[#0F2818] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const optionChartData = report?.design_options?.map((opt) => ({
    name: opt.name.length > 12 ? opt.name.substring(0, 12) + '...' : opt.name,
    carbon: opt.total_carbon,
  })) || [];

  const getRatingColor = (rating) => {
    const colors = {
      'Outstanding': 'bg-purple-100 text-purple-700',
      'Excellent': 'bg-green-100 text-green-700',
      'Very Good': 'bg-blue-100 text-blue-700',
      'Good': 'bg-yellow-100 text-yellow-700',
      'Pass': 'bg-orange-100 text-orange-700',
      'Unclassified': 'bg-slate-100 text-slate-700',
    };
    return colors[rating] || 'bg-slate-100 text-slate-700';
  };

  return (
    <div className="p-8" data-testid="reports-page">
      {/* Header */}
      <div className="page-header -mx-8 -mt-8 mb-8 px-8 py-6 bg-white border-b border-slate-200">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Sustainability Reports
            </h1>
            <p className="text-slate-600 mt-1">
              Generate comprehensive sustainability reports with BREEAM & LEED scoring
            </p>
          </div>
          {report && (
            <Button 
              variant="outline" 
              onClick={handleDownloadPdf}
              disabled={downloadingPdf}
              data-testid="download-pdf-button"
            >
              {downloadingPdf ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
                  Downloading...
                </span>
              ) : (
                <>
                  <FileDown className="w-4 h-4 mr-2" />
                  Download PDF
                </>
              )}
            </Button>
          )}
        </div>
      </div>

      {projects.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <FileText className="w-16 h-16 text-slate-300 mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              No Projects Available
            </h3>
            <p className="text-slate-600 text-center max-w-sm mb-6">
              Create a project with design options to generate sustainability reports
            </p>
            <Link to="/projects">
              <Button className="bg-[#0F2818] hover:bg-[#06140C]">
                Go to Projects
              </Button>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-8">
          {/* Project Selection */}
          <Card className="border-slate-200" data-testid="project-selection">
            <CardContent className="p-6">
              <div className="flex items-end gap-4">
                <div className="flex-1 max-w-md space-y-2">
                  <label className="text-sm font-medium text-slate-700">Select Project</label>
                  <Select value={selectedProjectId} onValueChange={setSelectedProjectId}>
                    <SelectTrigger data-testid="report-project-select">
                      <SelectValue placeholder="Choose a project" />
                    </SelectTrigger>
                    <SelectContent>
                      {projects.map((project) => (
                        <SelectItem key={project.id} value={project.id}>
                          {project.name} ({project.design_options?.length || 0} options)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  className="bg-[#0F2818] hover:bg-[#06140C]"
                  onClick={handleGenerateReport}
                  disabled={generating || !selectedProjectId || selectedProjectId === 'none'}
                  data-testid="generate-report-button"
                >
                  {generating ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Generating...
                    </span>
                  ) : (
                    <>
                      <FileText className="w-4 h-4 mr-2" />
                      Generate Report
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Report Content */}
          {report && (
            <div className="space-y-6 animate-fade-in">
              {/* Report Header */}
              <Card className="border-slate-200 bg-gradient-to-r from-[#0F2818] to-[#2D5A3F] text-white" data-testid="report-header">
                <CardContent className="p-8">
                  <div className="flex items-start justify-between">
                    <div>
                      <Badge className="bg-white/20 text-white mb-4">Sustainability Report</Badge>
                      <h2 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {report.project.name}
                      </h2>
                      <p className="text-white/80">
                        {report.project.building_type || 'Building'} • {report.project.location || 'Location not specified'}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-white/60 text-sm">Generated</p>
                      <p className="text-white font-medium">
                        {new Date(report.generated_at).toLocaleDateString('en-GB', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                        })}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Summary Stats */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <Card className="border-slate-200" data-testid="report-total-carbon">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-2">
                      <Leaf className="w-5 h-5 text-[#0F2818]" />
                      <span className="text-sm text-slate-500">Total Carbon</span>
                    </div>
                    <p className="text-2xl font-bold text-slate-900">
                      {(report.summary.total_embodied_carbon / 1000).toFixed(2)}
                      <span className="text-sm font-normal text-slate-500 ml-1">tCO2e</span>
                    </p>
                  </CardContent>
                </Card>

                <Card className="border-slate-200" data-testid="report-carbon-per-sqm">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-2">
                      <TrendingDown className="w-5 h-5 text-[#800020]" />
                      <span className="text-sm text-slate-500">Carbon Intensity</span>
                    </div>
                    <p className="text-2xl font-bold text-slate-900">
                      {report.summary.carbon_per_sqm || '—'}
                      <span className="text-sm font-normal text-slate-500 ml-1">kgCO2e/m²</span>
                    </p>
                  </CardContent>
                </Card>

                <Card className="border-slate-200" data-testid="report-budget-status">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-2">
                      {report.summary.budget_status === 'Within Budget' ? (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      ) : report.summary.budget_status === 'Over Budget' ? (
                        <AlertCircle className="w-5 h-5 text-red-500" />
                      ) : (
                        <Building2 className="w-5 h-5 text-slate-400" />
                      )}
                      <span className="text-sm text-slate-500">Budget Status</span>
                    </div>
                    <p className={`text-lg font-semibold ${
                      report.summary.budget_status === 'Within Budget'
                        ? 'text-green-600'
                        : report.summary.budget_status === 'Over Budget'
                        ? 'text-red-600'
                        : 'text-slate-500'
                    }`}>
                      {report.summary.budget_status}
                    </p>
                  </CardContent>
                </Card>

                <Card className="border-slate-200" data-testid="report-best-option">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-2">
                      <Star className="w-5 h-5 text-yellow-500" />
                      <span className="text-sm text-slate-500">Best Option</span>
                    </div>
                    <p className="text-lg font-semibold text-slate-900 truncate">
                      {report.summary.best_option?.name || '—'}
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Certification Tabs */}
              <Tabs defaultValue="breeam" className="space-y-6">
                <TabsList className="grid w-full max-w-md grid-cols-2">
                  <TabsTrigger value="breeam" data-testid="breeam-tab">BREEAM Assessment</TabsTrigger>
                  <TabsTrigger value="leed" data-testid="leed-tab">LEED Assessment</TabsTrigger>
                </TabsList>

                {/* BREEAM Tab */}
                <TabsContent value="breeam" className="space-y-6">
                  {report.certification?.breeam ? (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* BREEAM Summary */}
                      <Card className="border-slate-200" data-testid="breeam-summary">
                        <CardHeader className="border-b border-slate-100 pb-4">
                          <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            <Award className="w-5 h-5 text-[#0F2818]" />
                            BREEAM Mat 01 Rating
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="p-6">
                          <div className="text-center mb-6">
                            <Badge className={`text-lg px-4 py-2 ${getRatingColor(report.certification.breeam.rating)}`}>
                              {report.certification.breeam.rating}
                            </Badge>
                          </div>

                          <div className="space-y-4">
                            <div>
                              <div className="flex justify-between text-sm mb-2">
                                <span className="text-slate-600">Credits Earned</span>
                                <span className="font-semibold">
                                  {report.certification.breeam.total_credits_earned} / {report.certification.breeam.max_credits}
                                </span>
                              </div>
                              <Progress 
                                value={report.certification.breeam.credits_percentage} 
                                className="h-3"
                              />
                            </div>

                            <div className="grid grid-cols-2 gap-4 pt-4">
                              <div className="text-center p-3 bg-slate-50 rounded-lg">
                                <p className="text-2xl font-bold text-[#0F2818]">
                                  {report.certification.breeam.carbon_per_sqm}
                                </p>
                                <p className="text-xs text-slate-500">kgCO2e/m²</p>
                              </div>
                              <div className="text-center p-3 bg-slate-50 rounded-lg">
                                <p className="text-2xl font-bold text-[#800020]">
                                  {report.certification.breeam.credits_percentage}%
                                </p>
                                <p className="text-xs text-slate-500">Credit Score</p>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      {/* BREEAM Credits Breakdown */}
                      <Card className="border-slate-200" data-testid="breeam-breakdown">
                        <CardHeader className="border-b border-slate-100 pb-4">
                          <CardTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            Credits Breakdown
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="p-6">
                          <div className="space-y-4">
                            {Object.entries(report.certification.breeam.credits_breakdown || {}).map(([key, value]) => (
                              <div key={key} className="p-4 bg-slate-50 rounded-lg">
                                <div className="flex justify-between items-center mb-2">
                                  <span className="font-medium text-slate-900">
                                    {key.replace(/_/g, ' ').replace(/mat01/i, 'Mat 01').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                                  </span>
                                  <Badge variant="outline" className={value.earned === value.max ? 'bg-green-50 text-green-700' : ''}>
                                    {value.earned} / {value.max}
                                  </Badge>
                                </div>
                                <p className="text-sm text-slate-600">{value.status}</p>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>

                      {/* BREEAM Recommendations */}
                      <Card className="border-slate-200 lg:col-span-2" data-testid="breeam-recommendations">
                        <CardHeader className="border-b border-slate-100 pb-4">
                          <CardTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            Recommendations to Improve Score
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="p-6">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {(report.certification.breeam.recommendations || []).map((rec, index) => (
                              <div key={index} className="flex items-start gap-3 p-4 bg-[#E8EEEA] rounded-lg">
                                <Target className="w-5 h-5 text-[#0F2818] flex-shrink-0 mt-0.5" />
                                <p className="text-sm text-slate-700">{rec}</p>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  ) : (
                    <Card className="border-slate-200">
                      <CardContent className="flex flex-col items-center justify-center py-12">
                        <Award className="w-12 h-12 text-slate-300 mb-4" />
                        <p className="text-slate-600">BREEAM assessment requires gross floor area data</p>
                      </CardContent>
                    </Card>
                  )}
                </TabsContent>

                {/* LEED Tab */}
                <TabsContent value="leed" className="space-y-6">
                  {report.certification?.leed ? (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* LEED Summary */}
                      <Card className="border-slate-200" data-testid="leed-summary">
                        <CardHeader className="border-b border-slate-100 pb-4">
                          <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            <Award className="w-5 h-5 text-[#3B82F6]" />
                            LEED MR Credits
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="p-6">
                          <div className="text-center mb-6">
                            <Badge className="text-lg px-4 py-2 bg-blue-100 text-blue-700">
                              {report.certification.leed.certification_contribution}
                            </Badge>
                          </div>

                          <div className="space-y-4">
                            <div>
                              <div className="flex justify-between text-sm mb-2">
                                <span className="text-slate-600">Points Earned</span>
                                <span className="font-semibold">
                                  {report.certification.leed.total_points_earned} / {report.certification.leed.max_points}
                                </span>
                              </div>
                              <Progress 
                                value={report.certification.leed.points_percentage} 
                                className="h-3"
                              />
                            </div>

                            <div className="text-center p-4 bg-blue-50 rounded-lg">
                              <p className="text-3xl font-bold text-[#3B82F6]">
                                {report.certification.leed.points_percentage}%
                              </p>
                              <p className="text-sm text-slate-500">of available MR points</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      {/* LEED Points Breakdown */}
                      <Card className="border-slate-200" data-testid="leed-breakdown">
                        <CardHeader className="border-b border-slate-100 pb-4">
                          <CardTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            Points Breakdown
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="p-6">
                          <div className="space-y-4">
                            {Object.entries(report.certification.leed.points_breakdown || {}).map(([key, value]) => (
                              <div key={key} className="p-4 bg-slate-50 rounded-lg">
                                <div className="flex justify-between items-center mb-2">
                                  <span className="font-medium text-slate-900">
                                    {key.replace(/_/g, ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                                  </span>
                                  <Badge variant="outline" className={value.earned === value.max ? 'bg-blue-50 text-blue-700' : ''}>
                                    {value.earned} / {value.max}
                                  </Badge>
                                </div>
                                <p className="text-sm text-slate-600">{value.status}</p>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>

                      {/* LEED Recommendations */}
                      <Card className="border-slate-200 lg:col-span-2" data-testid="leed-recommendations">
                        <CardHeader className="border-b border-slate-100 pb-4">
                          <CardTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            Recommendations to Improve Score
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="p-6">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {(report.certification.leed.recommendations || []).map((rec, index) => (
                              <div key={index} className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg">
                                <Target className="w-5 h-5 text-[#3B82F6] flex-shrink-0 mt-0.5" />
                                <p className="text-sm text-slate-700">{rec}</p>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  ) : (
                    <Card className="border-slate-200">
                      <CardContent className="flex flex-col items-center justify-center py-12">
                        <Award className="w-12 h-12 text-slate-300 mb-4" />
                        <p className="text-slate-600">LEED assessment requires at least 2 design options</p>
                      </CardContent>
                    </Card>
                  )}
                </TabsContent>
              </Tabs>

              {/* Design Options Chart */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card className="border-slate-200" data-testid="report-chart">
                  <CardHeader className="border-b border-slate-100 pb-4">
                    <CardTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      Design Options Comparison
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-6">
                    {optionChartData.length > 0 ? (
                      <ResponsiveContainer width="100%" height={250}>
                        <BarChart data={optionChartData}>
                          <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                          <YAxis tickFormatter={(v) => `${(v / 1000).toFixed(1)}t`} />
                          <Tooltip
                            formatter={(value) => [`${(value / 1000).toFixed(2)} tCO2e`, 'Carbon']}
                            contentStyle={{ borderRadius: 6, border: '1px solid #E2E8F0' }}
                          />
                          <Bar dataKey="carbon" radius={[4, 4, 0, 0]}>
                            {optionChartData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="flex items-center justify-center h-64 text-slate-500">
                        No data available
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Design Options Table */}
                <Card className="border-slate-200" data-testid="report-options-table">
                  <CardHeader className="border-b border-slate-100 pb-4">
                    <CardTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      Design Options Detail
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead>Option</TableHead>
                          <TableHead className="text-right">Carbon</TableHead>
                          <TableHead className="text-right">Rank</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {report.design_options
                          ?.sort((a, b) => a.total_carbon - b.total_carbon)
                          .map((opt, index) => (
                            <TableRow key={opt.id}>
                              <TableCell className="font-medium">{opt.name}</TableCell>
                              <TableCell className="text-right font-mono text-[#0F2818]">
                                {(opt.total_carbon / 1000).toFixed(2)} tCO2e
                              </TableCell>
                              <TableCell className="text-right">
                                {index === 0 ? (
                                  <Badge className="bg-green-100 text-green-700">Best</Badge>
                                ) : (
                                  <span className="text-slate-500">#{index + 1}</span>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </div>

              {/* Footer */}
              <div className="text-center text-sm text-slate-500 py-4">
                <p>Report based on UK ICE Database v3.0 carbon factors</p>
                <p>BREEAM UK New Construction 2018 | LEED v4.1 BD+C criteria</p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Reports;
